import { useState, useRef, useEffect } from "react";
import axios from "axios";
import dayjs from "dayjs";
import { notification } from "antd";
import { CHAT_V2_API } from "./constants";
import { mapChatItem, fetchChatFilters, defaultChatFilterIndex, chatFilterSubConditions } from "./helpers";

const LARGE_LIST_THRESHOLD = 300;

// Reusable fetch/paginate/filter logic for a WATI chat list, scoped to a
// specific channel (or WATI's default channel when channelIds is omitted).
// Used both by WatiChatContext (customer channel, shared across QuerysetV4/
// TaskManagement) and standalone by WhatsappMessages (supplier channel) —
// same logic, deliberately separate state/data per channel, since the two
// channels serve genuinely different volumes and use cases.
//
// `seed` (optional) lets a caller avoid a redundant initial fetch by handing
// over an already-loaded background preload — see WatiChatContext's
// qv4Chatlist/supplierChatlist. It's only a one-time donor: once seeded, this
// hook's own state is authoritative and the seed is never re-synced from.
export function useWatiChatList({ authState, channelIds, seed } = {}) {
  const { list: seedList = [], loading: seedLoading = false, token: seedToken = null, count: seedCount = null } = seed || {};

  const [activeChats, setActiveChats] = useState([]);
  const [chatsLoading, setChatsLoading] = useState(false);
  const [chatsLoadingMore, setChatsLoadingMore] = useState(false);
  const [hasMoreChats, setHasMoreChats] = useState(true);
  const [chatSearch, setChatSearch] = useState("");
  const [chatSearchFilter, setChatSearchFilter] = useState(0);
  const [chatFilters, setChatFilters] = useState([]);
  const [chatFiltersLoading, setChatFiltersLoading] = useState(false);
  const [selectedFilterIndex, setSelectedFilterIndex] = useState(0);
  const [chatTotalCount, setChatTotalCount] = useState(null);
  // A plain ref, not state — pagination only ever reads this via the ref;
  // routing it through useState+effect previously meant "Load More" clicked
  // right after the initial fetch could still see a stale value.
  const chatContinuationTokenRef = useRef(null);
  const setChatContinuationToken = (token) => { chatContinuationTokenRef.current = token; };

  // Nudge staff toward search/filters instead of endlessly scrolling a huge
  // list — resets whenever a fresh (non-append) fetch starts a new search
  // context. Exempted for the "Unread" filter: someone who's already
  // filtered down to unread chats has done exactly what this nudge would
  // otherwise recommend.
  const largeListWarnedRef = useRef(false);
  const warnIfLargeList = (count) => {
    const filterName = chatFilters[selectedFilterIndex]?.name || "";
    if (/unread/i.test(filterName)) return;
    if (count > LARGE_LIST_THRESHOLD && !largeListWarnedRef.current) {
      largeListWarnedRef.current = true;
      notification.info({
        key: "chat-list-large",
        message: "That's a lot of chats",
        description: `You've loaded over ${LARGE_LIST_THRESHOLD} chats — try searching by name/number or picking a filter instead of scrolling through all of them.`,
        placement: "bottomLeft",
        duration: 8,
      });
    }
  };

  const currentFilterParams = (filterIdx = selectedFilterIndex) => {
    const f = chatFilters[filterIdx];
    return { filterType: f?.filterType ?? 0, subConditions: chatFilterSubConditions(f) };
  };

  const buildChatV2Payload = ({ search, searchOptionType, continuationToken = "", filterIdx }) => {
    const trimmed = (search || "").trim();
    const { filterType, subConditions } = currentFilterParams(filterIdx);
    const isMsgSearch = searchOptionType === 1 && trimmed;
    return {
      userId: authState?.id,
      continuationToken,
      // Customer UI omits this entirely (backend's own default applies,
      // matching how it's always worked); supplier UI passes its channel
      // explicitly, resolved from uzd_misc mode 'wati_channel_assignments'.
      ...(channelIds ? { channelIds } : {}),
      filterType,
      subConditions,
      searchString: trimmed || null,
      searchOptionType: trimmed ? searchOptionType : null,
      ...(isMsgSearch ? { from: dayjs().subtract(1, "year").toISOString(), to: dayjs().toISOString() } : {}),
    };
  };

  const fetchActiveChats = async (search = chatSearch, filter = chatSearchFilter, filterIdx = selectedFilterIndex) => {
    largeListWarnedRef.current = false;
    setChatsLoading(true);
    setHasMoreChats(true);
    try {
      const payload = buildChatV2Payload({ search, searchOptionType: filter, continuationToken: "", filterIdx });
      const res = await axios.post(CHAT_V2_API, payload, { headers: { accessToken: localStorage.getItem("accessToken") } });
      if (res.data.success) {
        const raw = res.data.data?.final_data || [];
        const mapped = raw.map(mapChatItem);
        setActiveChats(mapped);
        const token = res.data.data?.continuationToken || null;
        setChatContinuationToken(token);
        setHasMoreChats(!!token);
        setChatTotalCount(res.data.data?.count ?? null);
        warnIfLargeList(mapped.length);
        setChatsLoading(false);
        return mapped;
      }
    } catch {}
    setChatsLoading(false);
    return [];
  };

  const loadMoreChats = async () => {
    if (chatsLoadingMore || !hasMoreChats || chatsLoading) return;
    setChatsLoadingMore(true);
    try {
      const payload = buildChatV2Payload({ search: chatSearch, searchOptionType: chatSearchFilter, continuationToken: chatContinuationTokenRef.current || "" });
      const res = await axios.post(CHAT_V2_API, payload, { headers: { accessToken: localStorage.getItem("accessToken") } });
      if (res.data.success) {
        const raw = res.data.data?.final_data || [];
        const token = res.data.data?.continuationToken || null;
        setChatContinuationToken(token);
        let nextCount = 0;
        setActiveChats(prev => {
          const existingIds = new Set(prev.map(c => c.id));
          const merged = [...prev, ...raw.map(mapChatItem).filter(c => !existingIds.has(c.id))];
          nextCount = merged.length;
          return merged;
        });
        setHasMoreChats(!!token);
        warnIfLargeList(nextCount);
      }
    } catch {}
    setChatsLoadingMore(false);
  };

  // Chat filter dropdown ("All Chats" header) — populate from WATI's saved
  // filter tabs (account-wide, not channel-scoped), default to whichever tab
  // WATI marks isDefault.
  useEffect(() => {
    if (!authState?.id) return;
    setChatFiltersLoading(true);
    fetchChatFilters(authState.id).then(list => {
      setChatFilters(list);
      if (list.length > 0) {
        const idx = defaultChatFilterIndex(list);
        setSelectedFilterIndex(idx);
        if ((list[idx]?.filterType ?? 0) !== 0) fetchActiveChats(chatSearch, chatSearchFilter, idx);
      }
    }).finally(() => setChatFiltersLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState?.id]);

  const onSelectFilter = (idx) => {
    setSelectedFilterIndex(idx);
    fetchActiveChats(chatSearch, chatSearchFilter, idx);
  };

  // Seed from the background preload if available; fall back to a direct
  // fetch. Mount-once, plus a race-condition watcher for when the preload
  // resolves after this hook instance mounted. Deliberately NOT re-run on
  // every later seed-list change — that list keeps growing via live socket
  // messages, and resyncing on every change would clobber whatever
  // search/filter is currently active.
  useEffect(() => {
    if (seedList.length > 0) {
      setActiveChats(seedList.map(mapChatItem));
      setHasMoreChats(seedList.length >= 100);
      chatContinuationTokenRef.current = seedToken;
      setChatTotalCount(seedCount);
    } else if (!seedLoading) {
      fetchActiveChats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current || seedList.length === 0) return;
    seededRef.current = true;
    setActiveChats(prev => {
      if (prev.length > 0) return prev;
      chatContinuationTokenRef.current = seedToken;
      setChatTotalCount(seedCount);
      return seedList.map(mapChatItem);
    });
    setHasMoreChats(prev => seedList.length < 100 ? false : prev);
    setChatsLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedList]);

  return {
    activeChats, setActiveChats,
    chatsLoading, chatsLoadingMore, hasMoreChats, setHasMoreChats,
    chatSearch, setChatSearch, chatSearchFilter, setChatSearchFilter,
    chatFilters, chatFiltersLoading, selectedFilterIndex, setSelectedFilterIndex, chatTotalCount,
    setChatContinuationToken, chatContinuationTokenRef,
    buildChatV2Payload, currentFilterParams, fetchActiveChats, loadMoreChats, onSelectFilter,
  };
}
